import { analytics } from '../../../../Services/analytics';
import { MDBox } from '../../../../Component/Common/Components/MDBox';
import React, { useEffect } from 'react';
import { Autocomplete, Box, Card, CardContent, Grid, TextField } from '@mui/material';
import { reportTypeList, fileTypeList, EEReportTableMapper } from './EEReportingValidation';
import { StaticDataModel } from '../../../../Component/Model/Dto/StaticData';
import { EEReportGeneration, EEReportTable } from '../../../../Component/Model/Dto/EmploymentEquity/Reporting';
import { defaultObject } from '../../../../Helpers/typescriptHelper';
import { EntriesPerPage } from '../../../../Component/Model/Table/Table';
import { toast } from 'react-toastify';
import { ConfirmationDialog } from '../../../../Component/Common/Components/ConfirmationDialog';
import { useRecoilValue } from 'recoil';
import { ScorecardAtom } from '../../../../State/Scorecard.Atom';
import { EEReportService } from '../../../../Component/Api/Report/ReportService';
import config from '../../../../config';
import { DataTable } from '../../../../Component/Common/Components/DataTable/DataTable';
import { EEA2Service } from '../../../../Component/Api/EmploymentEquity/WorkforceProfile_EEA2/EEA2Service';
import { WorkforceProfileDto } from '../../../../Component/Model/Dto/EmploymentEquity/WorkforceProfile_EEA2/WorkforceProfile';
import { LoadingButton } from '@mui/lab';
import { UserActivityService } from '../../../../Component/Api/UserActivity/UserActivityService';
import { EEAReportType, UserActivityActionType } from '../../../../Component/Model/Enum/BEE123Enums';
import { EEAReportingSection } from '../../../../Component/Common/Constants/UserActivityTrackingSections';

export const EEReportingBase = () => {
	const [reportType, setReportType] = React.useState<StaticDataModel>(reportTypeList[0]);
	const [fileType] = React.useState<StaticDataModel>(fileTypeList[1]);
	const [reportData, setReportData] = React.useState<EEReportTable.Dto[]>(defaultObject);
	const [isLoading, setIsLoading] = React.useState<boolean>(false);
	const [entriesPerPage, setEntriesPerPage] = React.useState<EntriesPerPage>({ defaultValue: 5 });
	const [filteredItemsCount, setFilteredItemsCount] = React.useState<number>(0);
	const [totalItemsCount, setTotalItemsCount] = React.useState<number>(0);
	const [pageNumber, setPageNumber] = React.useState<number>(1);
	const [searchValue, setSearchValue] = React.useState<string>('');
	const [isGeneratingReport, setIsGeneratingReport] = React.useState<boolean>(false);
	const [selectedReportId, setSelectedReportId] = React.useState('');
	const [showConfirmationDialog, setShowConfirmationDialog] = React.useState<boolean>(false);
	const Scorecard = useRecoilValue(ScorecardAtom);
	const confirmDeleteMessage = 'This action cannot be reversed. Are you sure you want to delete this report?';

	const RenderReportTypeStep = () => {
		return (
			<Grid container mt={1}>
				<Grid item xs={12}>
					<Autocomplete
						options={reportTypeList}
						value={reportType}
						id="reportAutocomplete"
						getOptionLabel={(option) => option.Name}
						isOptionEqualToValue={(option, value) => option.Id === value.Id}
						disableClearable
						onChange={(_, data) => {
							if (data != null) {
								setReportType(data);
							}
						}}
						renderInput={(params) => <TextField label="Report" variant="outlined" {...params} id="reportType" />}
					/>
				</Grid>
			</Grid>
		);
	};

	const generateReport = async () => {
		try {
			setIsGeneratingReport(true);
			let reportRequest: EEReportGeneration.Request = {
				EEAReportType: reportType.Id as number,
				ReportExportType: fileType.Id as number,
				ScorecardId: Scorecard.scorecardId as string,
			};

			// Only for EEA2
			if (reportRequest.EEAReportType == 1) {
				//Get the headcounts
				const searchResult = await EEA2Service.GetEmployeeMovementHeadcountSummary(Scorecard.scorecardId);

				reportRequest.Recruitment = searchResult.data.recruitment.map(
					(section: WorkforceProfileDto.SummarySectionDto) => section.headcountValuesRow
				);
				reportRequest.Promotion = searchResult.data.promotion.map(
					(section: WorkforceProfileDto.SummarySectionDto) => section.headcountValuesRow
				);
				reportRequest.Termination = searchResult.data.termination.map(
					(section: WorkforceProfileDto.SummarySectionDto) => section.headcountValuesRow
				);
			}

			if (config.enable_new_EEA_Reports == 'true') {
				const response = await EEReportService.GenerateEEAReport(reportRequest);
				UserActivityService.createActivityLog({
					section: EEAReportingSection,
					actionType: UserActivityActionType.Generate,
					actionDescription: `Generate ${EEAReportType[reportRequest.EEAReportType]} report`,
					scorecardId: Scorecard.scorecardId,
					requestUrl: window.location.href
				})
				window.open(response.data.url, '_blank');
				response.data.warnings?.forEach((warning: string) => toast.warning(warning));
			} else {
				await EEReportService.GenerateReport(reportRequest);
				const request = `${config.publicUrl}/api/EEReport/DownloadLatestReport?scorecardId=${Scorecard.scorecardId}`;
				window.open(request, '_blank');
			}

			analytics.eeEeaReportGenerated();
			toast.success('Report successfully generated');
			fetchData();
		} catch (error: any) {
			if (error.response.status === 400) {
				toast.error(error.response.data);
			} else {
				toast.error('Unable to Generate Report');
			}
		} finally {
			setIsGeneratingReport(false);
		}
	};

	const entriesPerPageChanged = (entriesPerPage: number) => {
		setEntriesPerPage({ defaultValue: entriesPerPage });
		setPageNumber(1);
	};

	const pageNumberChanged = (pageNumber: number) => {
		setPageNumber(pageNumber);
	};

	const searchValueChanged = (searchValue: string) => {
		setSearchValue(searchValue);
	};

	const handleConfirmDelete = async (id: string) => {
		setSelectedReportId(id);
		setShowConfirmationDialog(true);
	};

	const handleDelete = async () => {
		try {
			await EEReportService.deleteReport(+selectedReportId);
			fetchData();
		} catch (error) {
			console.log(error);
		}
		handleCloseConfirmation();
	};

	const handleDownload = async (id: string) => {
		try {
			const response = await EEReportService.downloadReport(id, Scorecard.scorecardId);
			window.open(response.data, '_blank');
		} catch (error: any) {
			if (error.response != null) {
				toast.error(error.response.data);
			}
		}
	};

	const fetchData = async () => {
		setIsLoading(true);
		try {
			const ReportSearch: EEReportTable.SearchRequest = {
				PageNumber: pageNumber,
				ScorecardId: Scorecard.scorecardId,
				SearchValue: searchValue,
				PageSize: entriesPerPage.defaultValue!,
			};
			const response = await EEReportService.searchReport(ReportSearch);
			setReportData(response.data.data);
			setFilteredItemsCount(response.data.filteredItemsCount);
			setTotalItemsCount(response.data.totalItemsCount);
		} catch (error) {
			console.log(error);
		} finally {
			setIsLoading(false);
		}
	};

	const handleCloseConfirmation = () => {
		setShowConfirmationDialog(false);
		setSelectedReportId('');
	};

	useEffect(() => {
		fetchData();
	}, [entriesPerPage, pageNumber, searchValue]); // eslint-disable-line react-hooks/exhaustive-deps

	const reportTableData = EEReportTableMapper(reportData, handleDownload, handleConfirmDelete);
	return (
		<MDBox>
			<Grid container>
				<Grid item xs={5.8} xl={5.8} mt={2}>
					<form>
						<Card>
							<CardContent sx={{ width: '100%', padding: '10px', '&:last-child': { pb: 2 } }}>
								{RenderReportTypeStep()}
								<Box mt={2} style={{ display: 'flex', justifyContent: 'flex-end' }}>
									<LoadingButton loading={isGeneratingReport} variant="contained" color="primary" onClick={generateReport}>
										Generate Report
									</LoadingButton>
								</Box>
							</CardContent>
						</Card>
					</form>
				</Grid>

				<Grid item xs={12} mt={2}>
					<DataTable
						key={'ReportingTable'}
						table={reportTableData}
						entriesPerPage={entriesPerPage}
						pageNumber={pageNumber}
						filteredItemsCount={filteredItemsCount}
						totalItemsCount={totalItemsCount}
						entriesPerPageChanged={entriesPerPageChanged}
						pageNumberChanged={pageNumberChanged}
						isLoading={isLoading}
						canSearch
						searchValueChanged={searchValueChanged}
					></DataTable>
				</Grid>
			</Grid>

			<ConfirmationDialog
				showDialog={showConfirmationDialog}
				handleAccept={handleDelete}
				handleCancel={handleCloseConfirmation}
				heading={'Confirm Delete'}
				message={confirmDeleteMessage}
			/>
		</MDBox>
	);
};
